import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { Container, SimpleButton, ReportScoringBandsLegend, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import ExamComparisonFilters from './components/ExamComparisonFilters';
import ExamComparisonTable from './components/ExamComparisonTable';
import type { ExamComparisonDTO } from './models/examComparisonDTOs';
import type { FilterState } from './models/filterDTOs';
import { useAuth } from '@/src/app/context/useAuth';

const ExamComparisonPage: React.FC = () => {
    document.title = 'Exam Comparison';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<Array<ExamComparisonDTO>>([]);
    const [responseViewMode, setResponseViewMode] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const [showReportingBands, setShowReportingBands] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    viewMode: parsed.viewMode || 1,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examIds: parsed.examIds || [],
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            viewMode: 1,
            examTypeMasterId: 0,
            examIds: [],
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                viewMode: filterState.viewMode,
                examIds: filterState.examIds,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const refreshTableData = useCallback(
        async ({
            gradeLevelMasterId,
            districtId,
            schoolId,
            classId,
            examTypeMasterId,
            examIds,
            viewMode,
        }: FilterState = filterState) => {
            setError('');
            setLoaded(false);
            setHasSearched(true);

            // **Once there is more data in the DB, remove "/test" to use regular endpoint
            let requestUrl = `reports/examComparison?`;
            requestUrl += `districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&classId=${classId}`;
            requestUrl += `&gradeLevelMasterId=${gradeLevelMasterId}`;
            requestUrl += `&examTypeMasterId=${examTypeMasterId}`;
            const queryParams = examIds.map((id) => `examIds=${id}`).join('&');
            requestUrl += `&${queryParams}`;
            requestUrl += `&viewMode=${viewMode}`;

            try {
                const res = await apiRequest<ExamComparisonResponse>('GET', requestUrl);
                setData(res.data.data);
                setResponseViewMode(res.data.viewMode);
            } catch {
                setError('Failed to load Exam Comparison Report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    type ExamComparisonResponse = {
        viewMode: string;
        data: ExamComparisonDTO[];
    };

    useEffect(() => {
        const fs = filterState;
        if (fs.districtId && fs.gradeLevelMasterId && fs.examIds) {
            refreshTableData(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Exam Comparison Report</h1>
            <br />

            <ExamComparisonFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            <ErrorMessage error={hasSearched ? error : ''} />

            <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                <SimpleButton
                    variant="outlined"
                    onClick={() => setShowReportingBands(!showReportingBands)}
                    aria-expanded={showReportingBands}
                    aria-label="Toggle reporting bands dropdown"
                >
                    {showReportingBands ? 'Hide Scoring Bands' : 'Show Scoring Bands'}
                </SimpleButton>
            </Box>

            <Box sx={{ mb: showReportingBands ? 2 : 0 }}>
                <ReportScoringBandsLegend dataLoaded={loaded} showLegend={showReportingBands} />
            </Box>

            {hasSearched && (
                <ExamComparisonTable
                    data={data}
                    isLoading={!loaded}
                    error={error}
                    viewMode={responseViewMode}
                />
            )}
        </Container>
    );
};

export default ExamComparisonPage;
